import type { MarketAnalysisComparable, MarketSubjectSnapshot } from "../domain/types.ts";

export type MarketPriceLadderEntry = {
  id: string;
  label: string;
  askingPrice: number;
  subject: boolean;
  kind: "primary" | "competition" | "subject";
  position: number;
};

export type MarketPricePosition = {
  entries: MarketPriceLadderEntry[];
  median: number;
  lowest: number;
  highest: number;
  subjectPosition?: number;
  differenceFromMedian?: number;
  differenceFromMedianPercent?: number;
  insight?: string;
};

const median = (values: number[]) => {
  const sorted = [...values].sort((first, second) => first - second);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
};

export const buildPricePosition = (
  subject: MarketSubjectSnapshot,
  comparables: MarketAnalysisComparable[],
  competitors: MarketAnalysisComparable[] = []
): MarketPricePosition | undefined => {
  const primaryEntries = comparables.flatMap((comparable) => comparable.decision !== "excluded" && comparable.askingPrice
    ? [{ id: comparable.id, identity: comparable.sourceHash || comparable.id, label: comparable.street || comparable.locality || comparable.city, askingPrice: comparable.askingPrice, subject: false, kind: "primary" as const }]
    : []);
  const primaryIdentities = new Set(primaryEntries.map((entry) => entry.identity));
  const competitionEntries = competitors.flatMap((comparable) => {
    const identity = comparable.sourceHash || comparable.id;
    if (comparable.decision === "excluded" || !comparable.askingPrice || primaryIdentities.has(identity)) return [];
    primaryIdentities.add(identity);
    return [{ id: comparable.id, identity, label: comparable.street || comparable.locality || comparable.city, askingPrice: comparable.askingPrice, subject: false, kind: "competition" as const }];
  });
  const comparableEntries = [...primaryEntries, ...competitionEntries];
  if (comparableEntries.length === 0) return undefined;
  const comparablePrices = comparableEntries.map((entry) => entry.askingPrice);
  const marketMedian = median(comparablePrices);
  const subjectEntry = subject.askingPriceAtAnalysis
    ? [{ id: "subject", identity: "subject", label: "NÁŠ BYT", askingPrice: subject.askingPriceAtAnalysis, subject: true, kind: "subject" as const }]
    : [];
  const entries = [...comparableEntries, ...subjectEntry]
    .sort((first, second) => first.askingPrice - second.askingPrice || Number(second.subject) - Number(first.subject) || first.id.localeCompare(second.id))
    .map(({ identity: _identity, ...entry }, index) => ({ ...entry, position: index + 1 }));
  const subjectPosition = entries.find((entry) => entry.subject)?.position;
  const differenceFromMedian = subject.askingPriceAtAnalysis !== undefined
    ? Math.round(subject.askingPriceAtAnalysis - marketMedian)
    : undefined;
  const differenceFromMedianPercent = differenceFromMedian !== undefined && marketMedian > 0
    ? Math.round((differenceFromMedian / marketMedian) * 1000) / 10
    : undefined;
  const insight = differenceFromMedianPercent === undefined
    ? undefined
    : differenceFromMedianPercent > 2
      ? "Náš byt patrí medzi drahšie ponuky v porovnateľnej skupine. Lacnejšia konkurencia môže ovplyvniť reakciu trhu."
      : differenceFromMedianPercent < -2
        ? "Náš byt je cenovo pod mediánom porovnateľnej konkurencie."
        : "Náš byt je cenovo blízko mediánu porovnateľnej konkurencie.";
  return {
    entries,
    median: Math.round(marketMedian),
    lowest: Math.min(...comparablePrices),
    highest: Math.max(...comparablePrices),
    subjectPosition,
    differenceFromMedian,
    differenceFromMedianPercent,
    insight
  };
};
